import _ from 'lodash';
import { useState, useCallback, useEffect, MutableRefObject } from 'react';
import { useSizeCallback, useMount } from 'zoom/hooks';
import { MediaStream } from 'index-types';

/**
 * This function basically sets the dimension of the canvas, and watches the resize, and unexpected
 * changes. returns the dimension of canvas
 * @param mediaStream , a Zoom sdk mediastream
 * @param videoRef , a reference for a canvas element, containing video
 * @returns dimension, a dictinioray with width and height
 * #TODO: add types, revise logic
 */
export function useCanvasDimension(
  mediaStream: MediaStream | null,
  videoRef: MutableRefObject<HTMLCanvasElement | null>,
) {
  // setting dimension of canvas to state
  const [dimension, setDimension] = useState({ width: 0, height: 0 });
  /**
   * this function gets the size of a canvas, and then tries to resize it
   */
  const resizeCallback = ({ width, height }: { width: number; height: number }) => {
    // we check that ref has been initialized
    // TODO: Make it readable
    if (videoRef.current) {
      // this is a debounced version of setting the dimension
      const debouncedSetDimension = _.debounce((...args) => {
        setDimension({
          width: args[0],
          height: args[1],
        });
      }, 300);

      // calling the debounced function
      debouncedSetDimension(width, height);
    }
  };

  // registering the callback, and memorizing it, until video element changes
  const onCanvasResize = useCallback(resizeCallback, [videoRef]);

  // observing the size of a videoref, and passing the callback to act if it should be resized
  useSizeCallback(videoRef.current, onCanvasResize);

  // if the width and height has changed, the function would be changed and then trigger the recalculation.
  // #TODO: This is ugly
  useMount(() => {
    if (videoRef.current) {
      const { width, height } = videoRef.current.getBoundingClientRect();
      setDimension({ width, height });
    }
  });

  // this hook watches the current dimension and sets the dimension of an element, and if it
  // is unsettable, throws an error, which updates the canvas dimension in the stream
  // #TODO: This hsould be tested and revised
  useEffect(() => {
    const { width, height } = dimension;
    try {
      if (videoRef.current && width > 0 && height > 0) {
        videoRef.current.width = width;
        videoRef.current.height = height;
      }
    } catch (e) {
      mediaStream?.updateVideoCanvasDimension(videoRef.current as HTMLCanvasElement, width, height);
    }
  }, [mediaStream, dimension, videoRef]);
  return dimension;
}
